import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiChevronDown, FiCheck } from "react-icons/fi";
import {
  HeroWrapper,
  HeroBg,
  HeroBgVideo,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  ScrollIndicator,
  HeroSearchBar,
  HeroSearchSubmit,
} from "../styles/MFRealStateStyles";
import { MF_BASE } from "../basePath";
import { supabase } from "../../../services/supabaseClient";
import { getFilterOptions } from "../services/propertyService";

const HERO_BG_DEFAULT =
  "https://xtkorgedlxwfuaqyxguq.supabase.co/storage/v1/object/public/mf-imoveis/hero-bg.webp";
const VIDEO_EXTS = /\.(mp4|mov|webm|m4v)(\?|$)/i;

const TIPO_NEGOCIO_OPTS = [
  { value: "venda", label: "Comprar" },
  { value: "locacao", label: "Alugar" },
];

const TIPO_OPTS = [
  { value: "apartamento", label: "Apartamento" },
  { value: "casa", label: "Casa" },
  { value: "cobertura", label: "Cobertura" },
  { value: "terreno", label: "Terreno" },
  { value: "studio", label: "Studio" },
  { value: "comercial", label: "Comercial" },
];

function HeroDropdown({
  value,
  options,
  placeholder,
  onChange,
  allowClear = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);
  const isPlaceholder = !current;

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          height: 56,
          padding: "0 14px",
          background: "transparent",
          border: "none",
          color: isPlaceholder
            ? "var(--mf-text-muted)"
            : "var(--mf-text-primary)",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {current?.label || placeholder}
        </span>
        <FiChevronDown
          style={{
            flexShrink: 0,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "none",
            color: "var(--mf-text-muted)",
          }}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            margin: 0,
            padding: 6,
            listStyle: "none",
            background: "var(--mf-bg-card)",
            border: "1px solid var(--mf-border)",
            borderRadius: 10,
            boxShadow: "var(--mf-shadow-lg)",
            zIndex: 30,
            maxHeight: 320,
            overflowY: "auto",
            minWidth: "100%",
            width: "max-content",
            maxWidth: 280,
          }}
        >
          {allowClear && current && (
            <li>
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                style={dropdownItemStyle(false, true)}
              >
                Limpar seleção
              </button>
            </li>
          )}
          {options.map((opt) => {
            const selected = opt.value === value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={dropdownItemStyle(selected)}
                  onMouseEnter={(e) => {
                    if (!selected)
                      e.currentTarget.style.background =
                        "var(--mf-field-hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{opt.label}</span>
                  {selected && <FiCheck style={{ flexShrink: 0 }} />}
                </button>
              </li>
            );
          })}
          {options.length === 0 && (
            <li
              style={{
                padding: "12px 14px",
                color: "var(--mf-text-muted)",
                fontSize: 13,
              }}
            >
              Sem opções
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// Select com type-ahead: value e a chave do enum, mas o input filtra pela label.
// Diferente do HeroCombobox, NAO aceita texto livre — so opcoes da lista.
function HeroSearchableSelect({ value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Quando fecha sem selecionar, descarta a busca em andamento
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const norm = (s) =>
    (s || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const q = norm(query);
  const filtered = q ? options.filter((o) => norm(o.label).includes(q)) : options;

  const current = options.find((o) => o.value === value);
  const display = open ? query : current?.label || "";
  const isPlaceholder = !open && !current;

  const commit = (val) => {
    onChange(val);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", height: 56, padding: "0 14px" }}>
        <input
          type="text"
          value={display}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1, minWidth: 0,
            background: "transparent", border: "none", outline: "none",
            color: isPlaceholder ? "var(--mf-text-muted)" : "var(--mf-text-primary)",
            fontSize: 13, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase",
            fontFamily: "inherit",
          }}
        />
        <FiChevronDown
          onClick={() => setOpen((o) => !o)}
          style={{
            cursor: "pointer", flexShrink: 0,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "none",
            color: "var(--mf-text-muted)",
          }}
        />
      </div>

      {open && (
        <ul role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0,
            margin: 0, padding: 6, listStyle: "none",
            background: "var(--mf-bg-card)",
            border: "1px solid var(--mf-border)",
            borderRadius: 10, boxShadow: "var(--mf-shadow-lg)",
            zIndex: 30, maxHeight: 320, overflowY: "auto",
            minWidth: "100%", width: "max-content", maxWidth: 280,
          }}
        >
          {current && (
            <li>
              <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit("")}
                style={dropdownItemStyle(false, true)}>
                Limpar seleção
              </button>
            </li>
          )}
          {filtered.map((opt) => {
            const selected = opt.value === value;
            return (
              <li key={opt.value}>
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(opt.value)}
                  style={dropdownItemStyle(selected)}
                  onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "var(--mf-field-hover)"; }}
                  onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}>
                  <span>{opt.label}</span>
                  {selected && <FiCheck style={{ flexShrink: 0 }} />}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li style={{ padding: "12px 14px", color: "var(--mf-text-muted)", fontSize: 13 }}>
              Sem opções
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// Combobox: input editavel + dropdown filtravel pelas opcoes existentes.
// Aceita texto livre (user pode digitar algo que ainda nao esta na lista).
function HeroCombobox({ value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const norm = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  const q = norm(query);
  const filtered = q
    ? options.filter((o) => norm(o.label).includes(q))
    : options;

  const commit = (val) => {
    setQuery(val);
    onChange(val);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 56,
          padding: "0 14px",
        }}
      >
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange("");
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (query !== value) onChange(query);
          }}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--mf-text-primary)",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            fontFamily: "inherit",
          }}
        />
        <FiChevronDown
          onClick={() => setOpen((o) => !o)}
          style={{
            cursor: "pointer",
            flexShrink: 0,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "none",
            color: "var(--mf-text-muted)",
          }}
        />
      </div>

      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            margin: 0,
            padding: 6,
            listStyle: "none",
            background: "var(--mf-bg-card)",
            border: "1px solid var(--mf-border)",
            borderRadius: 10,
            boxShadow: "var(--mf-shadow-lg)",
            zIndex: 30,
            maxHeight: 320,
            overflowY: "auto",
            minWidth: "100%",
            width: "max-content",
            maxWidth: 320,
          }}
        >
          {query && (
            <li>
              <button
                type="button"
                onClick={() => commit("")}
                style={dropdownItemStyle(false, true)}
              >
                Limpar
              </button>
            </li>
          )}
          {filtered.map((opt) => {
            const selected = opt.value === value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(opt.value)}
                  style={dropdownItemStyle(selected)}
                  onMouseEnter={(e) => {
                    if (!selected)
                      e.currentTarget.style.background =
                        "var(--mf-field-hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{opt.label}</span>
                  {selected && <FiCheck style={{ flexShrink: 0 }} />}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li
              style={{
                padding: "12px 14px",
                color: "var(--mf-text-muted)",
                fontSize: 13,
              }}
            >
              Sem resultados — pressione Enter pra usar "{query}"
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function dropdownItemStyle(selected, muted = false) {
  return {
    width: "100%",
    padding: "11px 14px",
    background: selected ? "var(--mf-accent-warm)" : "transparent",
    color: selected
      ? "#fff"
      : muted
        ? "var(--mf-text-muted)"
        : "var(--mf-text-primary)",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: selected ? 600 : 500,
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    transition: "background 140ms ease",
  };
}

export default function HeroSection() {
  const navigate = useNavigate();
  const [heroBg, setHeroBg] = useState(HERO_BG_DEFAULT);
  const [heroPoster, setHeroPoster] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener
      ? mql.addEventListener("change", onChange)
      : mql.addListener(onChange);
    return () => {
      mql.removeEventListener
        ? mql.removeEventListener("change", onChange)
        : mql.removeListener(onChange);
    };
  }, []);

  const [tipoNegocio, setTipoNegocio] = useState("venda");
  const [tipo, setTipo] = useState("");
  const [cidade, setCidade] = useState("");
  const [condominio, setCondominio] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    cidades: [],
    condominiosByCity: {},
    tipos: [],
  });

  useEffect(() => {
    supabase
      .from("mf_config")
      .select("hero_image_url, hero_poster_url")
      .eq("id", 1)
      .single()
      .then(({ data }) => {
        if (data?.hero_image_url) setHeroBg(data.hero_image_url);
        if (data?.hero_poster_url) setHeroPoster(data.hero_poster_url);
      });
    getFilterOptions().then((opts) =>
      setFilterOptions({
        cidades: opts.cidades || [],
        condominiosByCity: opts.condominiosByCity || {},
        tipos: opts.tipos || [],
      }),
    );
  }, []);

  // Trocar cidade reseta condomínio
  const onChangeCidade = (v) => {
    setCidade(v);
    setCondominio("");
  };

  // Em mobile, nunca carrega video de fundo: iOS Low Power Mode bloqueia autoplay,
  // conexao movel lenta deixa o video preto enquanto buffer, e Save Data do
  // Chrome Android tambem bloqueia. Cai pro poster (imagem) sempre.
  const isVideo = !isMobile && VIDEO_EXTS.test(heroBg);
  const mobileBg = heroPoster || HERO_BG_DEFAULT;

  const cidadeOpts = filterOptions.cidades.map((c) => ({ value: c, label: c }));
  // So mostra tipos que de fato existem no cadastro (intersecao com a label canonica).
  const tipoOpts = TIPO_OPTS.filter((o) => filterOptions.tipos.includes(o.value));
  // Condominio so aparece quando a cidade selecionada tem condominios cadastrados.
  const condominiosDaCidade = cidade
    ? filterOptions.condominiosByCity[cidade] || []
    : [];
  const showCondominio = condominiosDaCidade.length > 0;
  const condominioOpts = [...new Set(condominiosDaCidade)]
    .sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }))
    .map((c) => ({ value: c, label: c }));

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (tipoNegocio) params.set("tipo_negocio", tipoNegocio);
    if (tipo) params.set("tipo", tipo);
    if (cidade) params.set("cidade", cidade);
    if (condominio) params.set("condominio", condominio);
    const qs = params.toString();
    navigate(`${MF_BASE}/imoveis${qs ? "?" + qs : ""}`);
  };

  // Mostra "Limpar" so quando algum filtro alem do default (Comprar) esta preenchido
  const hasFilters = Boolean(tipo || cidade || condominio) || tipoNegocio !== "venda";
  const handleClear = () => {
    setTipoNegocio("venda");
    setTipo("");
    setCidade("");
    setCondominio("");
  };

  const fieldStyle = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "stretch",
    borderRight: "1px solid var(--mf-hairline)",
  };

  return (
    <HeroWrapper>
      {isVideo ? (
        <HeroBgVideo $poster={heroPoster || HERO_BG_DEFAULT}>
          <video
            src={heroBg}
            poster={heroPoster || HERO_BG_DEFAULT}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        </HeroBgVideo>
      ) : (
        <HeroBg
          $src={isMobile && VIDEO_EXTS.test(heroBg) ? mobileBg : heroBg}
        />
      )}
      <HeroContent>
        <img
          src="/mf-real-state/logos/logo_mf_real_white.png"
          alt="MF Real Estate"
          style={{
            display: "block",
            margin: "0 auto 24px",
            height: 180,
            width: "auto",
            objectFit: "contain",
          }}
        />
        <HeroTitle>Imóveis que valem a visita</HeroTitle>
        <HeroSubtitle>
          Imóveis selecionados em Alphaville, Tamboré, Barueri para morar e
          investir
        </HeroSubtitle>
        <HeroSearchBar onSubmit={handleSearch} role="search">
          <div style={fieldStyle}>
            <HeroDropdown
              value={tipoNegocio}
              options={TIPO_NEGOCIO_OPTS}
              placeholder="Comprar"
              onChange={setTipoNegocio}
            />
          </div>
          <div style={fieldStyle}>
            <HeroSearchableSelect
              value={tipo}
              options={tipoOpts}
              placeholder="Tipo"
              onChange={setTipo}
            />
          </div>
          <div style={showCondominio ? fieldStyle : { ...fieldStyle, borderRight: "none" }}>
            <HeroCombobox
              value={cidade}
              options={cidadeOpts}
              placeholder="Cidade"
              onChange={onChangeCidade}
            />
          </div>
          {showCondominio && (
            <div style={{ ...fieldStyle, borderRight: "none" }}>
              <HeroCombobox
                value={condominio}
                options={condominioOpts}
                placeholder="Condomínio"
                onChange={setCondominio}
              />
            </div>
          )}
          {hasFilters && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Limpar filtros"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: "var(--mf-text-muted)",
                border: "none",
                borderLeft: "1px solid var(--mf-hairline)",
                padding: "0 16px",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "color 140ms ease, background 140ms ease",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--mf-text-primary)";
                e.currentTarget.style.background = "var(--mf-field-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--mf-text-muted)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              Limpar
            </button>
          )}
          <HeroSearchSubmit type="submit" aria-label="Pesquisar imóveis">
            <FiSearch />
          </HeroSearchSubmit>
        </HeroSearchBar>
      </HeroContent>
      <ScrollIndicator>Scroll</ScrollIndicator>
    </HeroWrapper>
  );
}
